/**
 * Defensive discovery of the four Wallet SCREENS this shell frames.
 *
 * THE PROBLEM
 * -----------
 * `frontend/web/src/wallet/{home,records,entry,stats}/` are built by four other agents,
 * in parallel with this one. A plain `import { WalletHome } from "../home"`
 * would make THIS package fail to build until THAT package exists — four ways
 * for the shell to be broken by someone else's timing. Rollup resolves static
 * imports at build time and a missing module is a hard error.
 *
 * THE FIX
 * -------
 * `import.meta.glob` resolves at BUILD time against whatever is actually on
 * disk, and a pattern that matches nothing yields an empty map rather than an
 * error. So the shell asks "is there a home screen?" instead of asserting there
 * is one, and renders the existing SPA page (`<Outlet/>`) when the answer is no.
 * Every screen is a separate lazy chunk either way, which is what the centre
 * add button wants anyway — the entry screen should not be in the first paint.
 *
 * THE CONTRACT (for the four screen agents)
 * -----------------------------------------
 * Put your screen at ONE of these, in this order of preference:
 *
 *     frontend/web/src/wallet/home/index.tsx        export function WalletHome()
 *     frontend/web/src/wallet/home/WalletHome.tsx   (same, if you prefer a named file)
 *
 * …and likewise `records/WalletRecords`, `entry/WalletEntry`,
 * `stats/WalletStats`. A `default` export is accepted as a fallback. Nothing
 * else in this file needs to change when your screen lands — it is found by
 * the glob on the next build.
 *
 * Read the period/account context with `useWalletShell()` from
 * `@/wallet/shell`. Close the entry sheet with its `closeEntry()`.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import type { EntryType } from "./context";
import type { EntryRecord } from "@/wallet/entry/types";

export type ScreenArea = "home" | "records" | "entry" | "stats";

/**
 * Props the shell passes to the entry screen — the intersection of what this
 * shell knows and what `@/wallet/entry` accepts (`WalletEntryProps`). Both are
 * optional there, so a screen that ignores them still works.
 */
export interface EntryScreenProps {
  onClose?: () => void;
  defaultType?: EntryType;
  /** F5 Clone: the entry to copy into a new one (see `openEntry`). */
  cloneFrom?: EntryRecord;
  /** Edit this saved record (see `openEntry`'s `edit`). */
  record?: EntryRecord;
}

type Loader = () => Promise<unknown>;

/**
 * Every plausible screen entry point, resolved at build time.
 *
 * The negative patterns matter more than they look. Vite emits a lazy chunk
 * for EVERY key this glob matches, whether or not the map is ever indexed by
 * it — so the wildcard "Wallet" pattern matching
 * `entry/WalletEntry.smoke.test.tsx` put a 1.6 MB test bundle (vitest,
 * magic-string, testing-library) into the production build. Excluding tests and
 * this package's own directory keeps the map to the four screens it is asking
 * about.
 */
const MODULES = import.meta.glob([
  "../*/index.tsx",
  "../*/index.ts",
  "../*/Wallet*.tsx",
  "!../shell/**",
  "!../**/*.test.*",
  "!../**/*.spec.*",
  "!../**/__*",
]) as Record<string, Loader>;

/** In preference order. The first that exists wins. */
const CANDIDATES: Record<ScreenArea, string[]> = {
  home: ["../home/index.tsx", "../home/index.ts", "../home/WalletHome.tsx"],
  records: ["../records/index.tsx", "../records/index.ts", "../records/WalletRecords.tsx"],
  entry: ["../entry/index.tsx", "../entry/index.ts", "../entry/WalletEntry.tsx"],
  stats: ["../stats/index.tsx", "../stats/index.ts", "../stats/WalletStats.tsx"],
};

const EXPORT_NAME: Record<ScreenArea, string> = {
  home: "WalletHome",
  records: "WalletRecords",
  entry: "WalletEntry",
  stats: "WalletStats",
};

function resolve<P extends object>(area: ScreenArea): LazyExoticComponent<ComponentType<P>> | null {
  const path = CANDIDATES[area].find((candidate) => candidate in MODULES);
  if (!path) return null;

  return lazy(async () => {
    const module = (await MODULES[path]()) as Record<string, unknown>;
    const named = module[EXPORT_NAME[area]] ?? module.default;
    if (typeof named !== "function") {
      // The module exists but exports something else. Reporting it here is far
      // more useful than a blank screen; the shell's fallback still applies
      // because `MissingExport` renders the explanation, not nothing.
      const message = `@/wallet/${area} exports neither ${EXPORT_NAME[area]} nor a default component`;
      console.error(message);
      const Placeholder = () => null;
      return { default: Placeholder as unknown as ComponentType<P> };
    }
    return { default: named as ComponentType<P> };
  });
}

/**
 * Resolved once at module load. `null` means "not built yet" and the shell
 * falls back to the SPA page the route table already renders.
 */
export const HomeScreen = resolve<Record<string, never>>("home");
export const RecordsScreen = resolve<Record<string, never>>("records");
export const StatsScreen = resolve<Record<string, never>>("stats");
export const EntryScreen = resolve<EntryScreenProps>("entry");

/** Which areas were found — surfaced in the More sheet's build badge. */
export function availableScreens(): ScreenArea[] {
  const found: ScreenArea[] = [];
  if (HomeScreen) found.push("home");
  if (RecordsScreen) found.push("records");
  if (EntryScreen) found.push("entry");
  if (StatsScreen) found.push("stats");
  return found;
}
